import { StanButton } from '@/components';
import { MFA } from '@/lib';
import { useEffect, useMemo, useRef, useState } from 'react';

export const OTPInput = ({
    error,
    warning,
    onResetErrors,
    submitButtonText,
    onSubmit,
    onShowRecovery,
    showHelpAndRecovery,
}: {
    error: string[];
    warning: string;
    onResetErrors: () => void;
    submitButtonText: string;
    onSubmit: (code: string) => void;
    onShowRecovery: () => void;
    showHelpAndRecovery: boolean;
}) => {
    const [otpDigits, setOtpDigits] = useState<string[]>(Array(MFA.OTP_LENGTH).fill(''));
    const [isLoading, setIsLoading] = useState(false);
    const otpInputRefs = useRef<HTMLInputElement[]>([]);

    const hasErrors = error?.length;

    useEffect(() => {
        if (hasErrors) {
            setIsLoading(false);
            setOtpDigits(Array(MFA.OTP_LENGTH).fill(''));
        }
    }, [hasErrors, setIsLoading, setOtpDigits]);

    const handleSubmit = (code: string) => {
        setIsLoading(true);
        onSubmit(code);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, idx = 0) => {
        e.preventDefault();
        const numericValue = e.clipboardData
            .getData('text')
            .replace(/\D/g, '')
            .slice(0, MFA.OTP_LENGTH - idx);

        if (!numericValue) return;

        const code = [...otpDigits];
        for (let i = 0; i < numericValue.length; i++) {
            code[idx + i] = numericValue[i] ?? '';
        }

        setOtpDigits(code);

        if (code.every(ch => ch && ch.length === 1) && code.length === MFA.OTP_LENGTH) {
            handleSubmit(code.join(''));
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
        const newDigits = [...otpDigits];
        newDigits[idx] = e.target.value;
        setOtpDigits(newDigits);
        onResetErrors();
        if (newDigits[idx]?.length && idx < MFA.OTP_LENGTH - 1) {
            const nextInput = otpInputRefs.current[idx + 1];
            if (nextInput) {
                nextInput.focus();
                nextInput.select();
            }
        }
    };

    const otpFilled = useMemo(() => otpDigits.every(digit => digit !== ''), [otpDigits]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
        // Handle backspace
        if (e.key === 'Backspace') {
            if (otpDigits[idx]?.length === 0 && idx > 0) {
                // If current input is empty, move to previous input
                const prevInput = otpInputRefs.current[idx - 1];
                if (prevInput) {
                    prevInput.focus();
                    prevInput.select();
                }
            } else {
                setOtpDigits(prev => {
                    const newDigits = [...prev];
                    newDigits[idx] = '';
                    return newDigits;
                });
            }
            return;
        }
        // Handle left arrow
        if (e.key === 'ArrowLeft' && idx > 0) {
            const prevInput = otpInputRefs.current[idx - 1];
            if (prevInput) {
                prevInput.focus();
                prevInput.select();
            }
            return;
        }
        // Handle right arrow
        if (e.key === 'ArrowRight' && idx < otpDigits.length - 1) {
            const nextInput = otpInputRefs.current[idx + 1];
            if (nextInput) {
                nextInput.focus();
                nextInput.select();
            }
            return;
        }
    };
    const onShowHelp = () => {};

    return (
        <form
            id='mfa-otp-form'
            onSubmit={e => {
                e.preventDefault();
                handleSubmit(otpDigits.join(''));
            }}
        >
            <div className='mt-3 mb-4 flex w-full flex-col text-center'>
                <div className='mx-auto my-0 flex content-between gap-2'>
                    {Array.from({ length: MFA.OTP_LENGTH }).map((_, idx) => (
                        <input
                            // eslint-disable-next-line react/no-array-index-key
                            key={idx}
                            value={otpDigits[idx]}
                            ref={el => {
                                if (el) {
                                    otpInputRefs.current[idx] = el;
                                }
                            }}
                            type='text'
                            pattern='[0-9]*'
                            inputMode='numeric'
                            maxLength={1}
                            onChange={e => handleChange(e, idx)}
                            onKeyDown={e => handleKeyDown(e, idx)}
                            onPaste={handlePaste}
                            placeholder=''
                            className={`${error ? 'error' : ''} text-brand-main border-brand-soft h-11 w-8 rounded-lg border-2 px-0 py-0.5 text-center text-2xl! font-semibold transition duration-300 outline-none md:h-16 md:w-14 md:p-0 md:text-4xl!`}
                        />
                    ))}
                </div>
                {(hasErrors || warning) && (
                    <div className='w-full pt-1.5 pl-1.5 text-sm'>
                        {hasErrors && (
                            <span className='text-negative font-semibold'>
                                <i />
                                {error[0]}
                            </span>
                        )}
                        {warning && (
                            <span className='text-warning'>
                                {hasErrors && <br />}
                                <i />
                                {warning}
                            </span>
                        )}
                    </div>
                )}
            </div>
            <StanButton
                name='mfa-validate-button'
                className='mb-2 w-full'
                disabled={!otpFilled}
                loading={isLoading}
                type='submit'
                size='md'
                data-target='#mfa-configuration-modal'
            >
                {submitButtonText}
            </StanButton>
            {showHelpAndRecovery ? (
                <div className='space-y-2 pt-5'>
                    <p className='modal-pretext text-light text-center'>
                        Lost access?{' '}
                        <a className='cursor-pointer' onClick={onShowRecovery}>
                            Use a backup code
                        </a>
                    </p>
                    <p className='modal-pretext text-light text-body-sm text-center'>
                        Or{' '}
                        <a
                            className='cursor-pointer'
                            href='https://help.stan.store/article/410-2fa-help'
                            target='_blank'
                        >
                            Get Help
                        </a>
                    </p>
                </div>
            ) : (
                <StanButton name='help-button' className='w-100' size='md' type='button' onClick={onShowHelp}>
                    Need help?
                </StanButton>
            )}
        </form>
    );
};
